<template>
  <div>
    <div class="row border-bottom white-bg dashboard-header campaign-header">
      <div class="col-lg-12">
        <h2 style="display: inline">Campaigns</h2>
        <campaign-filter class="pull-right" style="display: inline" @onFilter="handleFilter" />
      </div>
    </div>
    <div class="wrapper wrapper-content animated fadeInRight">
      <div class="row" v-if="$apollo.queries.campaignList.loading">
        <div class="col-lg-12">
          <loader message="Loading your campaigns" />
        </div>
      </div>

      <div
        class="row"
        v-if="
          !$apollo.queries.campaignList.loading &&
            campaignList.objects &&
            !campaignList.objects.length
        "
      >
        <div class="col-lg-12">
          <div class="empty">No campaigns found.</div>
        </div>
      </div>

      <div class="row" v-if="!$apollo.queries.campaignList.loading">
        <div class="col-lg-12">
          <campaign-list-item
            v-for="item in campaignList.objects"
            :key="item.id"
            :id="parseInt(item.id, 10)"
            :title="item.title"
            :live="item.live"
            :perpetual="item.perpetual"
            :progress="item.progress"
            :advertiser="item.campaignadvertiser && item.campaignadvertiser.advertiser"
            :startDate="moment(item.startDate)"
            :endDate="moment(item.endDate)"
            :createdBy="item.createdBy"
            :campaignType="item.campaignType"
            :totalMessages="item.engagements.total"
            :uniqueMessages="item.engagements.unique"
            :totalThisMonth="item.engagements.totalThisMonth"
            :totalPerDayThisMonth="item.engagements.totalPerDayThisMonth"
            :channelLinks="item.linkedcampaignchannelSet"
            :replyMessage="displayReplyMessage(item.replymessage)"
            :growth="item.engagements.growth"
            :isFavourite="item.favourite"
            :topEngagers="item.engagements.topEngagers"
            :survey="getSurvey(item.survey)"
            :bots="getBotsLinks(item.linkedcampaignchannelSet)"
            :leadCount="item.leadCount"
            @onRandomSelect="handleRandomSelect(item.id)"
            @onRandomSelections="handleRandomSelections(item.id)"
            @onDownloadMessages="handleDownloadMessages(item)"
            @onEditCampaign="handleCampaignEdit(item.id)"
            @onFavourite="handleFavourite($event, item.id)"
            @onToggleLiveStatus="handleToggleLiveStatus($event, item.id)"
            @onUnlink="handleUnlink($event, item.id)"
            @onSelectProfile="handleSelectProfile($event)"
            @onExportSurveyData="handleExportSurveyData(item.survey)"
            @onDownloadBotReport="handleDownloadBotReport(item)"
          />

          <profile-detail-dialog
            v-if="profileDialogVisible && selectedProfile"
            :originalProfile="selectedProfile"
            :updatedProfileMap="updatedProfileMap"
            :profileDialogVisible="profileDialogVisible"
            :profileId="getProfileId(selectedProfile)"
            :campaignId="selectedCampaignForProfile"
            @onDialogVisibilityChange="handleProfileDialogVisibilityChanged"
            @profileUpdated="handleProfileUpdated"
          />

          <div class="text-center" v-if="campaignList.pages > 1">
            <paginator
              id="paginator"
              :hasNext="campaignList.hasNext"
              :hasPrevious="campaignList.hasPrev"
              :pages="campaignList.pages"
              :currentPage="campaignList.page"
              v-if="campaignList.objects"
              @goToNextPage="handleNextPage"
              @goToPreviousPage="handlePreviousPage"
              @goToPage="handleGoToPage"
            ></paginator>
          </div>
        </div>
      </div>
    </div>
    <campaign-manage
      v-if="campaignManageVisible"
      :me="me"
      :campaignToEdit="campaignToEdit"
      :editingCampaign="editCampaign"
      :isVisible="campaignManageVisible"
      :addingInstagramChannel="addingInstagramChannel"
      :addingTwitterChannel="addingTwitterChannel"
      @onVisibleChanged="handleCreateCampaignVisibilityChanged"
      @onRequestTwitterAuth="handleRequestTwitterAuth"
      @onRequestInstagramAuth="handleRequestInstagramAuth"
      @onSaved="handleCampaignCreated"
    />
    <div v-if="campaignList && campaignList.objects && campaignList.objects.length > 0">
      <random-select-dialog
        v-if="randomSelectVisible"
        :isVisible="randomSelectVisible"
        :campaignId="randomSelectCampaignId"
        @onVisibleChanged="handleRandomSelectVisibilityChanged"
      />
      <random-selections-dialog
        v-if="randomSelectionsVisible"
        :isVisible="randomSelectionsVisible"
        :campaignId="randomSelectCampaignId"
        @onVisibleChanged="handleRandomSelectionsVisibilityChanged"
      />
      <download-messages-dialog
        v-if="downloadMessagesVisible"
        :channelLinks="selectedChannelLink['linkedcampaignchannelSet']"
        :isVisible="downloadMessagesVisible"
        :campaignId="parseInt(selectedChannelLink['id'], 10)"
        :campaignTitle="selectedChannelLink['title']"
        @onVisibleChanged="handleDownloadMessagesVisibilityChanged"
      />
      <bot-report-dialog
        v-if="downloadBotReportVisible"
        :channelLinks="getBotsLinks(selectedChannelLink['linkedcampaignchannelSet'])"
        :isVisible="downloadBotReportVisible"
        :campaignId="parseInt(selectedChannelLink['id'], 10)"
        :campaignTitle="selectedChannelLink['title']"
        @onVisibleChanged="handleDownloadBotReportVisibilityChanged"
      />
    </div>
    <can action="create" subject="campaigns">
      <fab-speed-dial
        position-type="fixed"
        icon-size="small"
        mainTooltip="New campaign"
        @click="handleCreateCampaign"
      />
    </can>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { has } from 'lodash';
import moment from 'moment';

import CampaignListItem from '@/components/campaign/CampaignListItem';
import CampaignManage from '@/components/campaign/CampaignManage';
import FabSpeedDial from '@/components/common/FabSpeedDial';
import Loader from '@/components/common/Loader';
import RandomSelectDialog from '@/components/campaign/RandomSelectDialog';
import RandomSelectionsDialog from '@/components/campaign/RandomSelectionsDialog';
import DownloadMessagesDialog from '@/components/campaign/DownloadMessagesDialog';
import BotReportDialog from '@/components/campaign/BotReportDialog';
import ProfileDetailDialog from '@/components/profile/ProfileDetailDialog';
import ProfileDialogMixin from '@/components/profile/ProfileDialogMixin';
import CampaignFilter from '@/components/campaign/CampaignFilter';
import Paginator from '@/components/common/Paginator';
import * as campaignTypes from '@/store/modules/campaign/mutation-types';
import Can from '@/components/permissions/Can';
import channelUnlinkMutation from '@/graphql/unlinkChannel.gql';
import favouriteCampaignMutation from '@/graphql/favouriteCampaign.gql';
import liveCampaignMutation from '@/graphql/liveCampaign.gql';
import campaignListQuery from '@/graphql/campaignList.gql';
import * as profileTypes from '@/store/modules/profile/mutation-types';

export default {
  name: 'CampaignDashboard',

  components: {
    CampaignListItem,
    CampaignManage,
    FabSpeedDial,
    Loader,
    Can,
    RandomSelectDialog,
    RandomSelectionsDialog,
    DownloadMessagesDialog,
    BotReportDialog,
    ProfileDetailDialog,
    CampaignFilter,
    Paginator,
  },

  mixins: [ProfileDialogMixin],
  computed: {
    ...mapState('user', ['me']),
    ...mapState('channel', ['addingInstagramChannel', 'addingTwitterChannel']),
    ...mapState('campaign', [
      'filterTitle',
      'filterType',
      'filterIsActive',
      'filterSortBy',
      'filterPage',
    ]),
    ...mapState('survey', [
      'surveys',
      'downloadingResponses',
      'downloadingResponsesFailed',
      'downloadingResponsesSucceeded',
    ]),
  },

  beforeDestroy() {
    this.$store.commit(`profile/${profileTypes.SHOW_PROFILES_DIALOG}`, false);
  },

  data: () => ({
    campaignList: [],
    moment,
    campaignManageVisible: false,
    randomSelectVisible: false,
    randomSelectionsVisible: false,
    randomSelectCampaignId: 0,
    downloadMessagesVisible: false,
    downloadBotReportVisible: false,
    downloadMessagesCampaignId: 0,
    downloadMessagesCampaignTitle: '',
    campaignToEdit: null,
    editCampaign: false,
    selectedCampaignForProfile: 0,
    selectedChannelLink: [],
  }),

  apollo: {
    campaignList: {
      query: campaignListQuery,
      prefetch: true,
      variables() {
        return {
          filterTitle: this.filterTitle,
          filterType: this.filterType,
          filterLive: this.filterIsActive,
          filterSortBy: this.filterSortBy,
          page: this.filterPage,
        };
      },
    },
  },
  methods: {
    handleSelectProfile(event) {
      this.selectedCampaignForProfile = event.campaignId;
      this.$store.commit(`profile/${profileTypes.SELECT_PROFILES_PROFILE}`, event.profile);
      this.$store.commit(`profile/${profileTypes.SHOW_PROFILES_DIALOG}`, true);
    },

    displayReplyMessage(replyMessage) {
      if (replyMessage) {
        return replyMessage.text;
      }
      return '';
    },

    handleNextPage() {
      this.handleGoToPage(this.filterPage + 1);
    },

    handlePreviousPage() {
      this.handleGoToPage(this.filterPage - 1);
    },

    handleGoToPage(page) {
      this.$store.commit(`campaign/${campaignTypes.SET_CAMPAIGN_PAGE}`, page);
    },

    handleCreateCampaign() {
      this.campaignToEdit = '';
      this.editCampaign = false;
      this.campaignManageVisible = true;
    },

    handleCampaignEdit(campaignId) {
      this.campaignToEdit = campaignId;
      this.editCampaign = true;
      this.campaignManageVisible = true;
    },

    handleCreateCampaignVisibilityChanged(visibility) {
      this.campaignManageVisible = visibility;
    },

    handleRandomSelectVisibilityChanged(visibility) {
      this.randomSelectVisible = visibility;
    },

    handleRandomSelectionsVisibilityChanged(visibility) {
      this.randomSelectionsVisible = visibility;
    },

    handleDownloadMessagesVisibilityChanged(visibility) {
      this.downloadMessagesVisible = visibility;
    },

    handleDownloadBotReportVisibilityChanged(visibility) {
      this.downloadBotReportVisible = visibility;
    },

    handleCampaignCreated() {
      this.campaignManageVisible = false;
      this.$apollo.queries.campaignList.refetch();
    },

    handleRequestTwitterAuth() {
      this.$store.dispatch('channel/addTwitterChannel', this.me.id);
    },

    handleRequestInstagramAuth() {
      this.$store.dispatch('channel/addInstagramChannel', this.me.id);
    },

    handleRandomSelect(campaignId) {
      this.randomSelectVisible = true;
      this.randomSelectCampaignId = parseInt(campaignId, 10);
    },

    handleRandomSelections(campaignId) {
      this.randomSelectionsVisible = true;
      this.randomSelectCampaignId = parseInt(campaignId, 10);
    },

    handleDownloadMessages(channelLinks) {
      this.selectedChannelLink = channelLinks;
      this.downloadMessagesVisible = true;
    },

    handleExportSurveyData(survey) {
      this.$store.dispatch('survey/downloadSurveyReport', survey);
    },

    handleDownloadBotReport(channelLinks) {
      this.selectedChannelLink = channelLinks;
      this.downloadBotReportVisible = true;
    },

    handleUnlink(linkId, campaignId) {
      this.$confirm('This will permanently unlink this channel?', 'Confirm', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning',
      }).then(() => {
        this.$apollo.mutate({
          mutation: channelUnlinkMutation,
          variables: {
            linkId,
          },
          update: store => {
            const data = store.readQuery(this.listQuery());
            const campaign = data.campaignList.objects.find(c => c.id === campaignId);
            const links = campaign.linkedcampaignchannelSet.filter(
              l => parseInt(l.id, 10) !== parseInt(linkId, 10),
            );
            campaign.linkedcampaignchannelSet = links;
            store.writeQuery({ ...this.listQuery(), data });
          },
        });
      });
    },

    handleFavourite(isFavourite, campaignId) {
      this.$apollo.mutate({
        mutation: favouriteCampaignMutation,
        variables: {
          id: campaignId,
          isFavourite,
        },
        update: store => {
          const data = store.readQuery(this.listQuery());
          const campaign = data.campaignList.objects.find(c => c.id === campaignId);
          campaign.favourite = isFavourite;

          // Sort the campaigns by favourites
          data.campaignList.objects.sort((a, b) => {
            if (a.favourite === b.favourite) {
              return 0;
            } else if (a.favourite) {
              return -1;
            }
            return 1;
          });

          store.writeQuery({ ...this.listQuery(), data });
        },
      });
    },

    handleToggleLiveStatus(isLive, campaignId) {
      this.$apollo.mutate({
        mutation: liveCampaignMutation,
        variables: {
          id: campaignId,
          isLive,
        },
        update: store => {
          const data = store.readQuery(this.listQuery());
          const campaign = data.campaignList.objects.find(c => c.id === campaignId);
          campaign.live = isLive;
          store.writeQuery({ ...this.listQuery(), data });
        },
      });
    },

    handleFilter({ title, type, isActive, sortBy }) {
      this.$store.commit(`campaign/${campaignTypes.SET_CAMPAIGN_FILTERS}`, {
        title,
        type,
        isActive,
        sortBy,
      });
    },

    listQuery() {
      return {
        query: campaignListQuery,
        variables: {
          filterTitle: this.filterTitle,
          filterType: this.filterType,
          filterLive: this.filterIsActive,
          filterSortBy: this.filterSortBy,
          page: this.filterPage,
        },
      };
    },

    getSurvey(id) {
      return this.surveys.find(survey => survey.id === id);
    },

    getBotsLinks(channelLinks) {
      return channelLinks.filter(handle => handle.channel.handle.match(/(\*[\d]+)+#/g));
    },
  },
  mounted() {
    if (has(this.$route.query, 'create')) this.handleCreateCampaign();
  },
};
</script>

<style lang="scss" scoped>
.campaign-header {
  border-top: 0px;
  padding: 18px;
}

#paginator {
  margin-bottom: 20px;
}

.empty {
  text-align: center;
  font-size: 1.6em;
  color: #ccc;
  text-shadow: 2px 2px 2px #efefef;
}
</style>



// WEBPACK FOOTER //
// CampaignDashboard.vue?f06939be